"use client";
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { motion } from 'framer-motion';
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerFavorites } from "@/hooks/useCustomerFavorites";
import { useCustomerPanelTranslations, useCustomerPanelUrlBuilder } from "@/lib/customer-panel-utils";
import { UrlBuilder } from "@/lib/url-builder";
import { extractErrorMessage } from "@/lib/customer-panel/error-handler";
import { ErrorMessage, LoadingSpinner } from "@/lib/customer-panel/ui-components";
import { fadeInVariants, cardVariants, microHoverVariants } from "@/lib/animations";
import { HeartIcon } from "@heroicons/react/24/outline";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import TourCard, { type TourProps } from "@/components/ui/TourCard";
import YachtCard from "@/components/ui/YachtCard";
import type { YachtProps } from "@/types/generalInterFaces";
import type { Favorite } from "@/services/CustomerApiService";
import { transformMediaUrl } from "@/lib/media-url-transformer";
import type { PageUrlMap } from "@/types/blog";

interface CustomerFavoritesProps {
  language?: string;
  languageId?: number;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

export default function CustomerFavorites({
  language = 'en',
  languageId,
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerFavoritesProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerFavorites',
    { language, translations, pageUrls },
    isBackendRender
  );

  const t = useCustomerPanelTranslations(translations);
  const buildUrl = useCustomerPanelUrlBuilder(pageUrls, language, defaultLanguage);
  const urlBuilder = useMemo(
    () => (pageUrls ? new UrlBuilder(pageUrls, language, defaultLanguage) : null),
    [pageUrls, language, defaultLanguage],
  );
  const [filter, setFilter] = useState<string>('all');
  const { isAuthenticated, customer, isLoading: authLoading } = useCustomerAuth();
  
  // Frontend'de gerçek hook'u kullan - TÜM favorileri çek (filtreleme client-side yapılacak)
  const favoritesHook = useCustomerFavorites({
    perPage: 100, // Tüm favorileri çekmek için yeterince büyük bir sayı
  });

  const allFavorites: Favorite[] = isBackendRender ? [
    {
      id: 1,
      product_id: 1,
      product_type: 'tour',
      product: {
        id: 1,
        name: 'Sample Tour',
        slug: 'sample-tour',
        type: 'tour',
      },
      created_at: new Date().toISOString(),
    },
    {
      id: 2,
      product_id: 2,
      product_type: 'yacht',
      product: {
        id: 2,
        name: 'Sample Yacht',
        slug: 'sample-yacht',
        type: 'yacht',
      },
      created_at: new Date().toISOString(),
    },
  ] : favoritesHook.favorites;
  const isLoading = isBackendRender ? false : favoritesHook.isLoading;
  const error = isBackendRender ? null : favoritesHook.error;
  const removeFavorite = isBackendRender ? async () => {} : favoritesHook.removeFavorite;

  // Filtrelenmiş favoriler - filter state'ine göre client-side filtreleme
  const favorites = useMemo(() => {
    if (filter === 'all') {
      return allFavorites;
    }
    return allFavorites.filter(fav => fav.product_type === filter);
  }, [allFavorites, filter]);

  // Favorilerdeki unique product type'ları bul (tab'lar için) - TÜM favorilere göre
  const availableProductTypes = useMemo(() => {
    const types = new Set<string>();
    allFavorites.forEach(fav => {
      if (fav.product_type) {
        types.add(fav.product_type);
      }
    });
    return Array.from(types).sort();
  }, [allFavorites]);

  // Artık backend'den gelen favorites response'unda product verisi tam (gallery, metas dahil)
  // Ayrı fetch yapmaya gerek yok - ProductSearchController formatProduct mantığı kullanılıyor

  // Helper: Parse images array from gallery JSON (ProductTourDetail ve backend formatına uygun)
  const parseImagesArray = useCallback((value: unknown): string[] => {
    if (!value) return [];
    
    // String ise JSON parse dene (backend'den gallery JSON string olarak gelebilir)
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.map((item: any) => {
            // String ise direkt transform et
        if (typeof item === 'string') {
          return transformMediaUrl(item);
        }
            // Object ise gallery item formatını kontrol et
        if (item && typeof item === 'object') {
              // type: 'image' kontrolü (backend gallery formatı)
              if (item.type === 'image' || !item.type) {
                // Öncelik sırası: url > src > path > media_id
          if (item.url) {
            return transformMediaUrl(item.url);
          }
                if (item.src) {
                  return transformMediaUrl(item.src);
                }
          if (item.path) {
            return transformMediaUrl(item.path);
          }
          if (item.media_id) {
            return `/api/media/proxy/${item.media_id}`;
          }
        }
            }
            return null;
          }).filter(Boolean) as string[];
        }
        // Array değilse string olarak transform et
        return [transformMediaUrl(value)];
      } catch {
        // Parse edilemezse string olarak transform et
        return [transformMediaUrl(value)];
      }
    }
    
    // Array ise gallery formatını kontrol et
    if (Array.isArray(value)) {
      return value.map((item: any) => {
        // String ise direkt transform et
        if (typeof item === 'string') {
          return transformMediaUrl(item);
        }
        // Object ise gallery item formatını kontrol et
        if (item && typeof item === 'object') {
          // type: 'image' kontrolü (backend gallery formatı)
          if (item.type === 'image' || !item.type) {
            // Öncelik sırası: url > src > path > media_id
            if (item.url) {
              return transformMediaUrl(item.url);
            }
            if (item.src) {
              return transformMediaUrl(item.src);
            }
            if (item.path) {
              return transformMediaUrl(item.path);
            }
            if (item.media_id) {
              return `/api/media/proxy/${item.media_id}`;
            }
          }
        }
        return null;
      }).filter(Boolean) as string[];
    }
    
    return [];
  }, []);

  // Helper: Parse image alt texts array
  const parseImageAltArray = useCallback((value: unknown, fallbackName: string, imageCount: number): string[] => {
    if (!value) {
      return imageCount > 0 ? Array(imageCount).fill(fallbackName) : [];
    }
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        const result = Array.isArray(parsed) ? parsed : [value];
        return result.length > 0 ? result : Array(imageCount).fill(fallbackName);
      } catch {
        return [value];
      }
    }
    return imageCount > 0 ? Array(imageCount).fill(fallbackName) : [];
  }, []);

  // Favorite'ı YachtProps formatına çevir (ProductSearchPageLayout'taki mapProductToYachtProps mantığını kullan)
  const mapFavoriteToYachtProps = useCallback((favorite: Favorite): YachtProps | null => {
    const product = favorite.product;
    if (!product || !product.slug || favorite.product_type !== 'yacht') return null;

    // Backend'den gelen product verisi zaten formatlanmış (ProductSearchController formatProduct mantığı)
    const fullProductData = product as any;
    
    const images = parseImagesArray(fullProductData.metas?.images || []);
    const imageAlt = parseImageAltArray(
      fullProductData.metas?.image_alt, 
      fullProductData.name || '', 
      images.length
    );
    
    const medias = images.map((img: string, index: number) => ({
      src: img,
      alt: imageAlt[index] || fullProductData.name,
    }));

    const finalPrice = fullProductData.calculated_price?.success && fullProductData.calculated_price.final_price > 0
      ? fullProductData.calculated_price.final_price
      : 0;

    const currencySymbol = fullProductData.currency?.symbol || fullProductData.calculated_price?.currency?.symbol || '€';

    const rawCurrency = fullProductData.calculated_price?.currency || fullProductData.currency;
    const currency = rawCurrency ? {
      id: (rawCurrency as any).id || 0,
      code: (rawCurrency as any).code || 'EUR',
      name: (rawCurrency as any).name || 'Euro',
      symbol: (rawCurrency as any).symbol || currencySymbol,
      symbol_position: (rawCurrency as any).symbol_position || 'before',
      thousands_separator: (rawCurrency as any).thousands_separator || ',',
      decimal_separator: (rawCurrency as any).decimal_separator || '.',
    } : {
      id: 0,
      code: 'EUR',
      name: 'Euro',
      symbol: currencySymbol,
      symbol_position: 'before',
      thousands_separator: ',',
      decimal_separator: '.',
    };

    const minRentalDuration = (fullProductData.calculated_price as any)?.min_rental_duration 
      ? String((fullProductData.calculated_price as any).min_rental_duration)
      : (fullProductData.metas?.min_rental_duration ? String(fullProductData.metas.min_rental_duration) : '');

    const guestCapacity = fullProductData.metas?.guest_capacity 
      || fullProductData.metas?.yacht_guest_capacity 
      || fullProductData.metas?.capacity 
      || fullProductData.metas?.pax_count
      ? parseInt(String(fullProductData.metas.guest_capacity || fullProductData.metas.yacht_guest_capacity || fullProductData.metas.capacity || fullProductData.metas.pax_count))
      : undefined;

    let startLocation = fullProductData.metas?.yacht_marina || fullProductData.metas?.start_location || '';
    if (startLocation && typeof startLocation === 'string' && startLocation.startsWith('[')) {
      try {
        const parsed = JSON.parse(startLocation);
        if (Array.isArray(parsed) && parsed.length > 0) {
          startLocation = parsed[0];
        } else if (typeof parsed === 'string') {
          startLocation = parsed;
        }
      } catch {
        // Ignore parse errors
      }
    }

    return {
      medias,
      title: fullProductData.name || `Product #${favorite.product_id}`,
      start: startLocation,
      rentType: fullProductData.metas?.rental_type || '',
      minRentDay: minRentalDuration,
      length: fullProductData.metas?.length || fullProductData.metas?.yacht_length || '',
      cabin: fullProductData.metas?.cabin_count || fullProductData.metas?.cabin || '',
      wc: fullProductData.metas?.bathroom_wc || fullProductData.metas?.wc ? parseInt(String(fullProductData.metas.bathroom_wc || fullProductData.metas.wc)) : 0,
      guestCapacity: guestCapacity,
      yachtClass: fullProductData.metas?.yacht_class || undefined,
      yachtType: fullProductData.metas?.yacht_type || undefined,
      builtYear: fullProductData.metas?.built_refit_year ? String(fullProductData.metas.built_refit_year) : undefined,
      beam: fullProductData.metas?.yacht_beam || undefined,
      flag: fullProductData.metas?.yacht_flag || undefined,
      brand: fullProductData.metas?.yacht_brand || undefined,
      price: {
        price: finalPrice > 0 ? `${currencySymbol}${finalPrice.toFixed(2)}` : `${currencySymbol}0.00`,
        type: fullProductData.metas?.price_type || 'Day',
      },
      code: fullProductData.metas?.code || `YACHT-${favorite.product_id}`,
      imageCount: images.length || (fullProductData.metas?.image_count ? parseInt(String(fullProductData.metas.image_count)) : 0),
      videoCount: fullProductData.metas?.video_count ? parseInt(String(fullProductData.metas.video_count)) : 0,
      slug: fullProductData.slug,
      url: urlBuilder
        ? urlBuilder.buildProductDetailUrl(
            fullProductData.type,
            fullProductData.slug,
            fullProductData.categories?.[0]?.slug,
          )
        : undefined,
      calculated_price: fullProductData.calculated_price ? {
        ...fullProductData.calculated_price,
        currency: currency,
        min_rental_duration: (fullProductData.calculated_price as any).min_rental_duration 
          ? (fullProductData.calculated_price as any).min_rental_duration 
          : (fullProductData.metas?.min_rental_duration ? parseInt(String(fullProductData.metas.min_rental_duration)) : undefined),
        used_months: (fullProductData.calculated_price as any).used_months || undefined,
      } : undefined,
    };
  }, [parseImagesArray, parseImageAltArray, urlBuilder]);

  // Favorite'ı TourProps formatına çevir (ProductSearchPageLayout'taki mapProductToTourProps mantığını kullan)
  // Backend'den gelen product verisi zaten formatlanmış (gallery, metas dahil)
  const mapFavoriteToTourProps = useCallback((favorite: Favorite): TourProps | null => {
    const product = favorite.product;
    if (!product || !product.slug) return null;

    // Backend'den gelen product verisi zaten formatlanmış (ProductSearchController formatProduct mantığı)
    // product objesi artık full product data içeriyor (metas, gallery, currency vb.)
    const fullProductData = product as any; // Backend'den gelen tam product data
    
    // ProductSearchPageLayout'taki gibi: metas.images kullan (backend formatProduct'da oluşturuluyor)
    const images = parseImagesArray(fullProductData.metas?.images || []);
    
    const imageAlt = parseImageAltArray(
      fullProductData.metas?.image_alt, 
      fullProductData.name || '', 
      images.length
    );
    
    // ProductSearchPageLayout'taki gibi diğer alanları da map et
    const productId = fullProductData.metas?.product_id ?? fullProductData.id ?? favorite.product_id;
    const finalPrice = fullProductData.calculated_price?.success && fullProductData.calculated_price.final_price > 0
      ? fullProductData.calculated_price.final_price
      : 0;
    
    const currency = fullProductData.calculated_price?.currency || fullProductData.currency || {
      symbol: '€',
      code: 'EUR',
      symbol_position: 'after',
      thousands_separator: ',',
      decimal_separator: '.',
    };
    const currencySymbol = currency.symbol || '€';
    
    const transfer = parseImagesArray(fullProductData.metas?.transfer) as string[];
    
    // Tour duration
    let tourDurationDay: number | undefined;
    let tourDurationNight: number | undefined;
    if (fullProductData.metas?.tour_duration) {
      let durationData = fullProductData.metas.tour_duration;
      if (typeof durationData === 'string') {
        try {
          durationData = JSON.parse(durationData);
        } catch {
          // Ignore parse errors
        }
      }
      if (typeof durationData === 'object' && durationData !== null) {
        tourDurationDay = durationData.day !== undefined ? Number(durationData.day) : undefined;
        tourDurationNight = durationData.night !== undefined ? Number(durationData.night) : undefined;
      }
    }
    
    // Tour route — öncelik: departure_city, station_city, end_city (ara duraklar)
    let tourRoute: Array<{
      location_type?: string;
      location_title?: string;
      location_description?: string;
      location_address?: string;
    }> = [];
    const departureCity = fullProductData.metas?.departure_city != null ? String(fullProductData.metas.departure_city).trim() : '';
    const endCity = fullProductData.metas?.end_city != null ? String(fullProductData.metas.end_city).trim() : '';
    let stationCity: string[] = [];
    if (fullProductData.metas?.station_city != null) {
      const raw = fullProductData.metas.station_city;
      if (Array.isArray(raw)) {
        stationCity = raw.map((s: unknown) => (s && typeof s === 'string' ? s.trim() : String(s).trim())).filter(Boolean);
      } else if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          stationCity = Array.isArray(parsed)
            ? parsed.map((s: unknown) => (s && typeof s === 'string' ? s.trim() : (s && typeof s === 'object' && s !== null && 'value' in s ? String((s as { value?: unknown }).value).trim() : String(s).trim()))).filter(Boolean)
            : [];
        } catch {
          stationCity = [raw.trim()].filter(Boolean);
        }
      }
    }
    if (departureCity || stationCity.length > 0 || endCity) {
      const parts: Array<{ location_title?: string }> = [];
      if (departureCity) parts.push({ location_title: departureCity });
      stationCity.forEach((s) => parts.push({ location_title: s }));
      if (endCity) parts.push({ location_title: endCity });
      if (parts.length > 0) tourRoute = parts;
    }
    if (tourRoute.length === 0 && fullProductData.metas?.tour_route) {
      let routeData = fullProductData.metas.tour_route;
      if (typeof routeData === 'string') {
        try {
          routeData = JSON.parse(routeData);
        } catch {
          routeData = [];
        }
      }
      if (Array.isArray(routeData)) {
        tourRoute = routeData;
      }
    }
    
    // Pricing periods
    let pricingPeriods: Array<{
      period_name?: string;
      period_start?: string;
      period_end?: string;
      quota?: number;
      packages?: Array<{
        package_name?: string;
        prices?: Array<{
          pax_range_id?: number;
          price_per_person?: number;
          single_supplement?: number;
          extra_bed_price?: number;
        }>;
      }>;
    }> = [];
    if (fullProductData.metas?.tour_pricing) {
      let pricingData = fullProductData.metas.tour_pricing;
      if (typeof pricingData === 'string') {
        try {
          pricingData = JSON.parse(pricingData);
        } catch {
          pricingData = [];
        }
      }
      if (Array.isArray(pricingData)) {
        pricingPeriods = pricingData;
      }
    }

    // Placeholder image URL (sadece resim yoksa kullan)
    const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=1200&fit=crop&auto=format';

    return {
      // Backend'den gelen metas.images kullan (ProductSearchController formatProduct'da oluşturuluyor)
      src: images.length > 0 ? images : [PLACEHOLDER_IMAGE],
      alt: imageAlt.length > 0 ? imageAlt : [fullProductData.name || ''],
      type: favorite.product_type,
      imageCount: images.length || (fullProductData.metas?.image_count ? parseInt(String(fullProductData.metas.image_count)) : 0),
      videoCount: fullProductData.metas?.video_count ? parseInt(String(fullProductData.metas.video_count)) : 0,
      title: fullProductData.name || `Product #${favorite.product_id}`,
      transferType: fullProductData.metas?.transfer_type || '',
      start: fullProductData.metas?.start_location || fullProductData.metas?.departure_city || '',
      stops: fullProductData.metas?.stops ?? (() => {
        const s = fullProductData.metas?.station_city;
        if (s == null) return '';
        if (Array.isArray(s)) return s.join(', ');
        if (typeof s === 'string') {
          try {
            const a = JSON.parse(s);
            return Array.isArray(a) ? a.join(', ') : s;
          } catch {
            return s;
          }
        }
        return String(s);
      })(),
      end: fullProductData.metas?.end_location || fullProductData.metas?.end_city || '',
      tourDuration: fullProductData.metas?.tour_duration || '',
      tourType: fullProductData.metas?.tour_type || fullProductData.metas?.group_type || '',
      tag: fullProductData.is_featured ? 'Featured' : favorite.product_type,
      is_featured: fullProductData.is_featured || false,
      price: {
        discount_price: finalPrice > 0 ? `${currencySymbol}${finalPrice.toFixed(2)}` : `${currencySymbol}0.00`,
        price: fullProductData.calculated_price?.base_price ? `${currencySymbol}${fullProductData.calculated_price.base_price.toFixed(2)}` : `${currencySymbol}${finalPrice.toFixed(2)}`,
      },
      calculated_price: fullProductData.calculated_price,
      base_price: fullProductData.base_price,
      currency: fullProductData.currency ? {
        id: fullProductData.currency.id,
        code: fullProductData.currency.code,
        symbol: fullProductData.currency.symbol,
        symbol_position: fullProductData.calculated_price?.currency?.symbol_position || 'after',
        thousands_separator: fullProductData.calculated_price?.currency?.thousands_separator || ',',
        decimal_separator: fullProductData.calculated_price?.currency?.decimal_separator || '.',
      } : null,
      reviews: fullProductData.metas?.reviews_count ? parseInt(String(fullProductData.metas.reviews_count)) : 0,
      tourId: productId ? String(productId) : (fullProductData.slug || 'unknown'),
      productId: productId ? String(productId) : String(favorite.product_id),
      brochureProductId: fullProductData?.id ?? favorite.product_id,
      slug: fullProductData.slug,
      url: urlBuilder
        ? urlBuilder.buildProductDetailUrl(
            fullProductData.type,
            fullProductData.slug,
            fullProductData.categories?.[0]?.slug,
          )
        : undefined,
      transfer: transfer.length > 0 ? transfer : undefined,
      tourDurationDay,
      tourDurationNight,
      tourRoute: tourRoute.length > 0 ? tourRoute : undefined,
      pricingPeriods: pricingPeriods.length > 0 ? pricingPeriods : undefined,
    };
  }, [parseImagesArray, parseImageAltArray, urlBuilder]);

  const handleRemoveFavorite = async (favoriteId: number) => {
    if (!removeFavorite || isBackendRender) return;
    
    if (confirm(t('customer_panel.favorites.remove', 'Remove from Favorites'))) {
      try {
        await removeFavorite(favoriteId);
      } catch (err) {
        // Failed to remove favorite - error handled by hook
      }
    }
  };

  // Oturum kontrolü - backend render modunda değilse kontrol et
  if (!isBackendRender && (!isAuthenticated || !customer) && !authLoading) {
    // ProtectedRoute zaten yönlendirme yapacak, burada sadece null döndür
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8" role="status" aria-live="polite">
        <LoadingSpinner size="lg" label={t('customer_panel.common.loading', 'Loading...')} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div {...backendAttributes} className="container mx-auto px-4 py-8">
        <ErrorMessage message={`${t('customer_panel.common.error', 'An error occurred')}: ${error}`} />
      </div>
    );
  }

  // Empty state
  if (favorites.length === 0) {
    return (
      <motion.div 
        {...backendAttributes} 
        className="container mx-auto px-4 py-8"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('customer_panel.favorites.title', 'My Favorites')}
          </h1>
        </div>
        <motion.div 
          className="bg-white rounded-lg drop-shadow-card border border-gray-200 p-12 text-center group hover:shadow-2xl transition-all duration-300"
          variants={cardVariants}
          initial="hidden"
          animate="visible"
        >
          <HeartIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">
            {t('customer_panel.favorites.empty', 'You don\'t have any favorites yet.')}
          </p>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      {...backendAttributes}
      variants={fadeInVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div 
        className="mb-6"
        variants={fadeInVariants}
        initial="hidden"
        animate="visible"
      >
        <h1 className="text-3xl font-bold text-gray-900">
          {t('customer_panel.favorites.title', 'My Favorites')}
        </h1>
      </motion.div>

      {/* Filters - Sadece favorilerde olan product type'ları göster */}
      {availableProductTypes.length > 0 && (
        <motion.div 
          className="mb-6 flex flex-wrap gap-2" 
          role="group" 
          aria-label={t('customer_panel.favorites.filters', 'Filter favorites')}
          variants={fadeInVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.button
            type="button"
            key="all"
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-all ${
              filter === 'all'
                ? 'bg-alsero-first text-white shadow-sm'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-alsero-first/30'
            }`}
            aria-pressed={filter === 'all'}
            variants={microHoverVariants}
            whileHover="hover"
            whileTap="tap"
          >
            {t('customer_panel.favorites.filter_all', 'All')}
          </motion.button>
          {availableProductTypes.map((productType, index) => (
            <motion.button
              type="button"
              key={productType}
              onClick={() => setFilter(productType)}
              className={`px-4 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 transition-all ${
                filter === productType
                  ? 'bg-alsero-first text-white shadow-sm'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-alsero-first/30'
              }`}
              aria-pressed={filter === productType}
              variants={microHoverVariants}
              whileHover="hover"
              whileTap="tap"
              custom={index}
            >
              {t(`customer_panel.favorites.filter_${productType}`, productType.charAt(0).toUpperCase() + productType.slice(1))}
            </motion.button>
          ))}
        </motion.div>
      )}

      {/* Favorites List - TourCard ve YachtCard ile göster */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {favorites.map((favorite, index) => {
          // Yacht product'ları için YachtCard kullan
          if (favorite.product_type === 'yacht') {
            const yachtProps = mapFavoriteToYachtProps(favorite);
            
            if (!yachtProps) {
              return (
                <motion.div
                  key={favorite.id}
                  className="bg-white rounded-lg shadow-tour-card border border-gray-200 p-6"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  custom={index}
                >
                  <p className="text-gray-500">
                    {favorite.product?.name || `Product #${favorite.product_id}`}
                  </p>
                </motion.div>
              );
            }

            return (
              <div key={favorite.id} className="relative group">
                <YachtCard
                  yacht={yachtProps}
                  viewType="box"
                  index={index}
                  language={language}
                  languageCode={language}
                  languageId={languageId}
                  pageUrls={pageUrls}
                  defaultLanguage={defaultLanguage}
                />
                {/* Remove from favorites button - overlay */}
                <motion.button
                  type="button"
                  onClick={() => handleRemoveFavorite(favorite.id)}
                  className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm text-red-600 hover:bg-red-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-lg opacity-0 group-hover:opacity-100"
                  aria-label={`${t('customer_panel.favorites.remove', 'Remove from Favorites')}: ${favorite.product?.name || `Product #${favorite.product_id}`}`}
                  variants={microHoverVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <HeartIcon className="h-5 w-5 fill-current" aria-hidden="true" />
                </motion.button>
              </div>
            );
          }

          // Tour ve diğer product'lar için TourCard kullan
          const tourProps = mapFavoriteToTourProps(favorite);
          
          if (!tourProps) {
            return (
              <motion.div
                key={favorite.id}
                className="bg-white rounded-lg shadow-tour-card border border-gray-200 p-6"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                custom={index}
              >
                <p className="text-gray-500">
                  {favorite.product?.name || `Product #${favorite.product_id}`}
                </p>
              </motion.div>
            );
          }

          return (
            <div key={favorite.id} className="relative group">
              <TourCard
                tour={tourProps}
                viewType="box"
                index={index}
                language={language}
                languageId={languageId}
                pageUrls={pageUrls}
                defaultLanguage={defaultLanguage}
              />
              {/* Remove from favorites button - overlay */}
              <motion.button
                type="button"
                onClick={() => handleRemoveFavorite(favorite.id)}
                className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm text-red-600 hover:bg-red-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-lg opacity-0 group-hover:opacity-100"
                aria-label={`${t('customer_panel.favorites.remove', 'Remove from Favorites')}: ${favorite.product?.name || `Product #${favorite.product_id}`}`}
                variants={microHoverVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <HeartIcon className="h-5 w-5 fill-current" aria-hidden="true" />
              </motion.button>
            </div>
          );
        })}
      </motion.div>
    </motion.div>
  );
}

